import React from "react";

// Source
import StoreRole from "../../store/Role";
import StoreRoute from "../../store/Route";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelRole from "../../model/Role";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ApiClientContact from "../../api/custom/ClientContact";
import * as ModelClientContact from "../../model/custom/ClientContact";
import ControllerAlert from "../Alert";

export default class ControllerClientContactProfile {
    // Variable
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelClientContact.Idata | undefined>(undefined);
    private dataMain = React.useRef<ModelClientContact.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelClientContact.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelClientContact.IselectOption | undefined>>;

    getDataMain: ModelClientContact.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelClientContact.Idata | undefined>>;

    getValidationForm: ModelClientContact.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelClientContact.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIdValue: string;
    setElementClientIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelClientContact.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMain, setDataMain] = React.useState<ModelClientContact.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getValidationForm, setValidationForm] = React.useState<ModelClientContact.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIdValue, setElementClientIdValue] = React.useState<string>("");
        this.getElementClientIdValue = getElementClientIdValue;
        this.setElementClientIdValue = setElementClientIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_client_contact_profile", () => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;

            if (historyState) {
                const parameterList = historyState.parameterList as ModelClientContact.Idata;

                if (parameterList) {
                    this.dataMain.current = { ...parameterList };
                } else {
                    this.dataMain.current = {} as ModelClientContact.Idata;
                }
            } else {
                this.dataMain.current = {} as ModelClientContact.Idata;
            }

            this.setDataMain(this.dataMain.current);

            this.apiValidationForm();

            this.apiSelectOption();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRoleData.current, this.storageRouteData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        this.setApiLoadingList((prev) => ({
            ...prev,
            profileChange: true
        }));

        if (this.dataMain.current && this.dataMain.current.id) {
            const id = this.dataMain.current.id;
            const client_id = this.readHtmlElementValue("client_id") as unknown as number;
            const department = this.readHtmlElementValue("department");
            const first_name = this.readHtmlElementValue("first_name");
            const last_name = this.readHtmlElementValue("last_name");
            const first_name_kanji = this.readHtmlElementValue("first_name_kanji");
            const last_name_kanji = this.readHtmlElementValue("last_name_kanji");
            const first_name_kana = this.readHtmlElementValue("first_name_kana");
            const last_name_kana = this.readHtmlElementValue("last_name_kana");
            const phone_number = this.readHtmlElementValue("phone_number");
            const email = this.readHtmlElementValue("email");
            const is_reviewer = this.readHtmlElementCheckbox("is_reviewer");
            const is_active = this.readHtmlElementCheckbox("is_active");

            ApiClientContact.updateData(
                id,
                client_id,
                department,
                first_name,
                last_name,
                first_name_kanji,
                last_name_kanji,
                first_name_kana,
                last_name_kana,
                phone_number,
                email,
                is_reviewer,
                is_active,
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        } else {
            ApiClientContact.createData(
                this.readHtmlElementValue("client_id") as unknown as number,
                this.readHtmlElementValue("department"),
                this.readHtmlElementValue("first_name"),
                this.readHtmlElementValue("last_name"),
                this.readHtmlElementValue("first_name_kanji"),
                this.readHtmlElementValue("last_name_kanji"),
                this.readHtmlElementValue("first_name_kana"),
                this.readHtmlElementValue("last_name_kana"),
                this.readHtmlElementValue("phone_number"),
                this.readHtmlElementValue("email"),
                this.readHtmlElementCheckbox("is_reviewer"),
                this.readHtmlElementCheckbox("is_active"),
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_client_contact_profile");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiClientContact.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelClientContact.IselectOption);
        });
    };

    private readHtmlElementValue = (tag: string): string => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.value : "";
    };

    private readHtmlElementCheckbox = (tag: string): boolean => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.checked : false;
    };

    private apiValidationForm = (): void => {
        ApiClientContact.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelClientContact.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };
}
